import React, { FC, useEffect, useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import loadable from '@loadable/component';
import { useLocation } from '@reach/router';
import classNames from 'classnames';
import useScreenRecognition from 'hooks/useScreenRecognition';
import { debounce } from 'lodash';

import 'styles/main.scss';
import './Layout.scss';

import Footer from 'components/Footer';
// import EurolandAI from '../../components/EurolandAI';
import Loadable from 'components/Loadable';
import Navigation from 'components/Navigation';
import SearchModal from 'components/SearchModal';
import { isBrowser } from 'utils/browser';

import { ElasticContextProvider } from '../../contexts/elasticContext';
import LanguageContext from '../../contexts/languageContext';
import { ModalContextProvider } from '../../contexts/modalContext';
import ScreenRecognitionContext from '../../contexts/screenRecognitionContext';
import { SharePriceContextProvider } from '../../contexts/sharePriceContext';
import { LayoutProps } from './models';

import 'components/FontawesomeIcons';

const PreviewWidget = loadable(() => import('components/PreviewWidget'));

const Layout: FC<LayoutProps> = ({
  children,
  headerTransparent,
  className,
  filters,
  indexName,
  pageId,
  numberOfResultsPerLoad,
  resultsSubTags
}) => {
  const { siteSettings } = useStaticQuery(graphql`
    query {
      siteSettings {
        lang
      }
    }
  `);

  const { pathname } = useLocation();

  const { lang } = siteSettings;
  const screenResolution = useScreenRecognition();
  const screenResolutionProviderValues = useMemo(() => ({ screenResolution }), [screenResolution]);

  let pageClass = '';

  const CSSclassNames = classNames('layout', className, {
    'layout--transparent': headerTransparent,
  });

  const shouldLoadPreviewWidget = Boolean(Number(process.env.GATSBY_ENABLE_PREVIEW_WIDGET));

  const setViewHeight = () => {
    if (isBrowser()) {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    }
  };

  const handleResize = debounce(() => {
    setViewHeight();
  }, 100);

  if (pathname == '/') {
    pageClass = 'layout__homepage';
  } else if (pathname == '/our-company/our-strategy/') {
    pageClass = 'layout__our-strategy-page';
  } else if (pathname == '/our-science/') {
    pageClass = 'layout__our-science-page';
  } else if (pathname == '/investors/') {
    pageClass = 'layout__investors-page';
  } else if (pathname == '/our-company/') {
    pageClass = 'layout__our-company-page';
  } else if (pathname == '/our-company/our-purpose/') {
    pageClass = 'layout__our-purpose-page';
  } else if (pathname == '/our-company/mead-johnson-nutrition/') {
    pageClass = 'layout__mead-johnson-nutrition-page';
  } else if (pathname == '/our-company/essential-home/') {
    pageClass = 'layout__essential-home-page';
  } else if (pathname == '/powerbrands/') {
    pageClass = 'layout__powerbrands-page';
  }
  useEffect(() => {
    setViewHeight();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ScreenRecognitionContext.Provider value={screenResolutionProviderValues.screenResolution}>
      <LanguageContext.Provider value={lang}>
        <ModalContextProvider>
          <ElasticContextProvider
            indexName={indexName}
            filters={filters}
            pageId={pageId}
            numberOfResultsPerLoad={numberOfResultsPerLoad}
            resultsSubTags={resultsSubTags}
          >
            <SharePriceContextProvider>
              <div className={CSSclassNames}>
                <Loadable isActive={shouldLoadPreviewWidget}>
                  <PreviewWidget cmsUrl={`${process.env.GATSBY_UMBRACO_BASE_URL}/umbraco`} />
                </Loadable>
                <Navigation />
                <SearchModal />

                <main id="main" className={`layout__main ${pageClass}`} tabIndex={-1}>
                  {children}
                </main>

                <Footer />
                {/*<EurolandAI />*/}
              </div>
              <div id="modal" />
            </SharePriceContextProvider>
          </ElasticContextProvider>
        </ModalContextProvider>
      </LanguageContext.Provider>
    </ScreenRecognitionContext.Provider>
  );
};

export default Layout;
